import { DoneOutlined } from "@mui/icons-material";
import { Box, Card, Chip, Skeleton, Stack, styled, Typography } from "@mui/material";
import React from "react";
import { Img } from "react-image";
import { cacheService, mediaService, thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";

export interface TitlePosterOrCoverMediaProps {
  kind: "artwork" | "file";
  id: number;
  height: number;
  width: number;
  name: string;
}

interface TitlePosterOrCoverCardProps extends TitlePosterOrCoverMediaProps {
  mode: "poster" | "cover";
  isNew: boolean;
  isSelected: boolean;
  isCurrent: boolean;
  onClick: () => void;
}

function GreatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : GreatestCommonDivisor(b, a % b);
}

function AspectRatio(width: number, height: number): string {
  const divisor = GreatestCommonDivisor(width, height);
  return `${width / divisor}:${height / divisor}`;
}

const PosterOrCoverMediaCard = (props: TitlePosterOrCoverCardProps) => {
  const { mode, kind, id, height, width, name, isNew, isSelected, isCurrent, onClick } = props;
  const aspectRatio = React.useMemo(() => AspectRatio(width, height), [width, height]);
  const imageUrl = React.useMemo(
    () =>
      kind === "artwork"
        ? thumbnailService.GetThumbRefByObjectId(id, cacheService.GetAuthKey()!, ThumbnailSize.small)
        : mediaService.GetFileImagePreviewer(id, cacheService.GetAuthKey()!),
    [id, kind]
  );

  return (
    <StyledCard isSelected={isSelected} onClick={onClick}>
      <Stack direction="row" alignItems="center">
        <Img
          src={imageUrl}
          loading="lazy"
          loader={<Skeleton height={"100%"} variant="rectangular" width={mode === "poster" ? 56 : 150} />}
          container={(children) => (
            <Box
              sx={{
                height: 84,
                aspectRatio: mode === "poster" ? "2 / 3" : "16 / 9",
                img: { height: "100%", objectFit: "cover" },
              }}
            >
              {children}
            </Box>
          )}
        />
        <Stack py={1.5} px={2} width={"100%"} spacing={1} justifyContent="flex-start">
          <Typography variant="body1" sx={(t) => ({ ...t.helpers(t).clampedText })}>
            {name}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip label={`${width}x${height}`} size="small" />
            <Chip label={aspectRatio} size="small" />
            {isNew && <Chip label="New" color="info" size="small" />}
            {isCurrent && <Chip label="Current" color="primary" size="small" />}
          </Stack>
        </Stack>
        {isSelected && <DoneOutlined color="primary" />}
      </Stack>
    </StyledCard>
  );
};

export default PosterOrCoverMediaCard;

const StyledCard = styled(Card, { shouldForwardProp: (prop) => prop !== "isSelected" })<{ isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    cursor: "pointer",
    width: "100%",
    paddingRight: theme.spacing(3),
    border: "1px solid",
    borderColor: theme.palette.backgroundAlpha.border,
    background: theme.palette.background.paper,
    ...(!isSelected && {
      "&:hover": {
        backgroundColor: theme.palette.backgroundAlpha.hover,
      },
    }),
    ...(isSelected && {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.alpha,
    }),
  })
);
